/**
 * Panda CSS styles for HALO Badge component
 *
 * Uses Class Variance Authority (cva) for systematic variant generation
 * Implements pill shape with tone-based colors and hover states for interactive badges
 */

import { css, cva } from '@/styled-system/css';

/**
 * Base badge styles shared across all variants
 * Defines pill shape, typography, and fundamental layout properties
 */
export const badgeBase = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px', // Space between icon and text
  borderRadius: '63px', // Pill shape per specification
  border: 'none',
  fontFamily: 'inherit',
  fontSize: '13px', // Per specification
  fontWeight: '700', // Per specification
  lineHeight: '25px',
  color: 'hpxWhite', // Per specification
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  cursor: 'default',
  outline: 'none',
  verticalAlign: 'middle', // Align with surrounding text

  // Interactive badges (when onClick is provided)
  '&[role="button"]': {
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease-in-out',

    // Focus styles for accessibility
    _focusVisible: {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: 'hpxTeal500',
      outlineOffset: '2px',
    },

    // Disabled state for interactive badges
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  },
});

/**
 * Badge variant styles using Class Variance Authority (cva)
 * Uses compound variants to combine tone + interactive combinations systematically
 */
export const badgeVariants = cva({
  variants: {
    tone: {
      primary: {}, // Colors defined in compound variants
      alert: {},
      inactive: {},
    },

    size: {
      sm: {
        height: '25px', // Consistent 25px height for all badges
        paddingX: '8px',
        fontSize: '12px',
      },
      md: {
        height: '25px', // Per specification
        paddingX: '10.5px', // Per specification (2px vertical padding handled by flexbox)
        fontSize: '13px', // Per specification
      },
    },

    interactive: {
      true: {}, // Hover states in compound variants
      false: {},
    },
  },

  // Compound variants for tone + interactive combinations
  // Each tone has both interactive and non-interactive versions
  compoundVariants: [
    // ==========================================
    // PRIMARY TONE VARIANTS
    // ==========================================

    // Primary - Non-interactive (static span)
    {
      tone: 'primary',
      interactive: false,
      css: {
        backgroundColor: 'hpxTeal500',
        color: 'hpxWhite',
      },
    },

    // Primary - Interactive (clickable button)
    {
      tone: 'primary',
      interactive: true,
      css: {
        backgroundColor: 'hpxTeal500',
        color: 'hpxWhite',

        _hover: {
          backgroundColor: 'hpxTeal600',
        },

        _active: {
          backgroundColor: 'hpxTeal700',
        },
      },
    },

    // ==========================================
    // ALERT TONE VARIANTS
    // ==========================================

    // Alert - Non-interactive (static span)
    {
      tone: 'alert',
      interactive: false,
      css: {
        backgroundColor: 'hpxRed400',
        color: 'hpxWhite',
      },
    },

    // Alert - Interactive (clickable button)
    {
      tone: 'alert',
      interactive: true,
      css: {
        backgroundColor: 'hpxRed400',
        color: 'hpxWhite',

        _hover: {
          backgroundColor: 'hpxRed500',
        },

        _active: {
          backgroundColor: 'hpxRed600',
        },
      },
    },

    // ==========================================
    // INACTIVE TONE VARIANTS
    // ==========================================

    // Inactive - Non-interactive (static span)
    // Uses 60% opacity on hpxGrey600 per specification
    {
      tone: 'inactive',
      interactive: false,
      css: {
        backgroundColor: 'rgba(110, 110, 110, 0.6)', // hpxGrey600 at 60% opacity
        color: 'hpxWhite',
      },
    },

    // Inactive - Interactive (clickable button)
    // Even though "inactive", still provides hover feedback when clickable
    {
      tone: 'inactive',
      interactive: true,
      css: {
        backgroundColor: 'rgba(110, 110, 110, 0.6)',
        color: 'hpxWhite',

        _hover: {
          backgroundColor: 'rgba(110, 110, 110, 0.8)', // Slightly more opaque on hover
        },

        _active: {
          backgroundColor: 'hpxGrey600', // Full opacity when pressed
        },
      },
    },
  ],

  defaultVariants: {
    tone: 'primary',
    size: 'md',
    interactive: false,
  },
});

/**
 * Icon wrapper for proper spacing and alignment within badges
 * Icons are slightly smaller than text for visual balance
 */
export const iconWrapper = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: '12px', // Slightly smaller than badge text for visual balance
  lineHeight: 1,
});
